import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Zap,
  Download,
  ChevronRight,
  BookOpen,
  Lock,
  Clock,
  ArrowDown,
  ArrowUpRight,
  Monitor,
  Chrome,
  Brain,
  Target,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize
} from 'lucide-react';
import UserNavbar from '../components/UserNavbar';
import { LogoIcon } from '../components/Logo';
import Footer from '../components/Footer';
import config from '../../config/env';
import { HeroStage } from '../../Demo';

// Video Player Component
const VideoPlayer = ({ src, poster, title, id }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const videoRef = useRef(null);

  const handlePlayPause = () => {
    const video = videoRef.current;
    if (video) {
      if (isPlaying) {
        video.pause();
      } else {
        video.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleMute = () => {
    const video = videoRef.current;
    if (video) {
      video.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleFullscreen = () => {
    const video = videoRef.current;
    if (video) {
      if (video.requestFullscreen) {
        video.requestFullscreen();
      } else if (video.webkitRequestFullscreen) {
        video.webkitRequestFullscreen();
      }
    }
  };

  if (!src) {
    return (
      <div className="relative w-full aspect-[16/10] bg-[#0a0a0a] border border-white/10 rounded-lg overflow-hidden group">
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="w-16 h-16 rounded-full border-2 border-white/20 flex items-center justify-center mb-4 group-hover:border-accent-color/50 transition-colors">
            <Play className="h-6 w-6 text-white/40 group-hover:text-accent-color transition-colors ml-1" />
          </div>
          <p className="text-white/40 text-sm font-mono tracking-wide">{title || 'VIDEO COMING SOON'}</p>
        </div>
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}
        />
      </div>
    );
  }

  return (
    <div className="relative w-full aspect-[16/10] bg-[#0a0a0a] border border-white/10 rounded-lg overflow-hidden group">
      <video
        ref={videoRef}
        id={id}
        className="w-full h-full object-contain bg-black"
        poster={poster}
        onEnded={() => setIsPlaying(false)}
        onClick={handlePlayPause}
      >
        <source src={src} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Video Controls Overlay */}
      <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <button
              onClick={handlePlayPause}
              className="p-1.5 sm:p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              {isPlaying ? (
                <Pause className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              ) : (
                <Play className="h-4 w-4 sm:h-5 sm:w-5 text-white ml-0.5" />
              )}
            </button>
            <button
              onClick={handleMute}
              className="p-1.5 sm:p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              {isMuted ? (
                <VolumeX className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              ) : (
                <Volume2 className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              )}
            </button>
          </div>
          <button
            onClick={handleFullscreen}
            className="p-1.5 sm:p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <Maximize className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
          </button>
        </div>
      </div>

      {/* Play Button Overlay (when paused) */}
      {!isPlaying && (
        <div
          className="absolute inset-0 flex items-center justify-center cursor-pointer"
          onClick={handlePlayPause}
        >
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-white/20 transition-colors">
            <Play className="h-6 w-6 sm:h-8 sm:w-8 text-white ml-1" />
          </div>
        </div>
      )}
    </div>
  );
};

// Guide Video Toggle Component
const GuideVideoToggle = () => {
  const [activeTab, setActiveTab] = useState('mcq');

  return (
    <div>
      {/* Toggle Buttons */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('mcq')}
          className={`px-4 py-2 text-sm font-mono tracking-wide border transition-all duration-200 ${activeTab === 'mcq'
              ? 'bg-white text-black border-white'
              : 'bg-transparent text-white/60 border-white/20 hover:border-white/40 hover:text-white'
            }`}
        >
          MCQ GUIDE
        </button>
        <button
          onClick={() => setActiveTab('coding')}
          className={`px-4 py-2 text-sm font-mono tracking-wide border transition-all duration-200 ${activeTab === 'coding'
              ? 'bg-white text-black border-white'
              : 'bg-transparent text-white/60 border-white/20 hover:border-white/40 hover:text-white'
            }`}
        >
          CODING GUIDE
        </button>
      </div>

      {/* Video Player */}
      <div className="relative">
        {activeTab === 'mcq' && (
          <VideoPlayer
            src={config.videos.guideMcq}
            title="MCQ TUTORIAL"
            id="landing-guide-mcq-video"
          />
        )}
        {activeTab === 'coding' && (
          <VideoPlayer
            src={config.videos.guideCoding}
            title="CODING TUTORIAL"
            id="landing-guide-coding-video"
          />
        )}
      </div>

      {/* Video Description */}
      <p className="text-white/40 text-sm mt-4">
        {activeTab === 'mcq'
          ? 'Learn how to use keyboard shortcuts for solving MCQ questions efficiently.'
          : 'Learn how to use the AI-powered coding assistant for programming problems.'
        }
      </p>
    </div>
  );
};

const Landing = () => {
  const capabilities = [
    {
      icon: Brain,
      title: 'Conceptual Learning',
      description: 'Understand complex topics with AI-powered explanations tailored to your learning level.',
    },
    {
      icon: BookOpen,
      title: 'Study Assistant',
      description: 'Get step-by-step guidance to master difficult concepts and improve comprehension.',
    },
    {
      icon: Target,
      title: 'Exam Preparation',
      description: 'Practice with intelligent feedback to build confidence before your assessments.',
    },
    {
      icon: Zap,
      title: 'Instant Explanations',
      description: 'Receive clear, detailed explanations in seconds to accelerate your learning.',
    },
    {
      icon: Lock,
      title: 'Privacy First',
      description: 'Your study sessions are private. We never store your learning data or personal information.',
    },
    {
      icon: Clock,
      title: 'Learn Efficiently',
      description: 'Save hours of searching with instant, accurate educational content at your fingertips.',
    },
  ];

  const steps = [
    { num: '01', title: 'Download', desc: 'Get the browser extension' },
    { num: '02', title: 'Developer Mode', desc: 'Enable in chrome://extensions' },
    { num: '03', title: 'Load Unpacked', desc: 'Select the extension folder' },
    { num: '04', title: 'Start Learning', desc: 'Begin your study session' },
  ];

  return (
    <div className="min-h-screen bg-[#111111]">
      <UserNavbar />

      {/* Hero Section - xAI Style */}
      <section className="border-b border-[#111111] relative w-full overflow-hidden pb-px md:overflow-x-hidden h-[calc(100svh-40px)] min-h-[calc(100svh-40px)] sm:h-[calc(100svh-44px)] sm:min-h-[calc(100svh-44px)]">
        {/* Gradient Background Container */}
        <div className="relative h-full w-full">
          {/* Gradient Overlay with blur effect */}
          <div
            className="absolute -inset-y-[25%] -right-24 flex w-[100vw] flex-col blur md:-right-6 md:w-[1200px]"
            style={{
              maskImage: 'linear-gradient(to right, rgba(255, 255, 255, 0), rgb(255, 255, 255))',
              opacity: 1
            }}
          >
            {/* Top gradient half */}
            <div
              className="grow"
              style={{
                background: 'conic-gradient(from 180deg at 99.78% 35% in lab, rgb(255, 255, 255) 18deg, rgb(255, 208, 134) 36deg, rgba(17, 17, 17, 0) 90deg, rgba(17, 17, 17, 0) 342deg, rgb(255, 255, 255) 360deg)'
              }}
            />
            {/* Bottom gradient half */}
            <div
              className="grow"
              style={{
                background: 'conic-gradient(from 0deg at 99.78% 65% in lab, rgb(255, 255, 255) 0deg, rgba(17, 17, 17, 0) 18deg, rgba(17, 17, 17, 0) 270deg, rgb(255, 208, 134) 324deg, rgb(255, 255, 255) 342deg)'
              }}
            />
          </div>

          {/* Content Container */}
          <div className="absolute inset-0 z-20">
            <div className="mx-auto w-full px-4 lg:px-6 xl:max-w-7xl flex h-full flex-col">
              {/* Main content area */}
              <div className="flex flex-1 w-full items-center">
              <div className="grid w-full items-center gap-12 lg:grid-cols-[1fr_1.05fr]">
                <div className="flex flex-col max-w-xl lg:pr-6">
                <h1
                  className="text-4xl md:text-5xl lg:text-6xl font-normal leading-[1.1] tracking-[-0.02em] text-white"
                >
                  Welcome to NeoPass{' '}
                  <span className="px-2 py-0.5 rounded bg-accent-custom text-black z-10">Free</span>
                </h1>

                <p className="mt-6 text-base md:text-lg text-white/55 max-w-lg leading-relaxed">
                  Now <span className="px-2 py-0.5 rounded bg-accent-custom text-black">free</span> for
                  <span className="text-accent"> Examly</span>,
                  <span className="text-accent"> iamneo</span>, and
                  <span className="text-accent"> Cisco NetAcad</span> - without taking over your page.
                </p>



                <div className="mt-8">
                  <div
                    className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-xl border border-white/20 bg-white hover:bg-white/90 hover:text-black transition-colors text-black text-sm font-mono tracking-wide"
                  >
                    <a
                      href={config.extensionDownloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-2"
                    >
                      NEOPASS {config.extensionVersion} : NOW AVAILABLE
                    </a>
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </div>
                </div>
                </div>

                <div className="flex justify-center lg:justify-end">
                  <HeroStage
                    className="w-full"
                    style={{ color: '#F0F0F5' }}
                  />
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges
      <section className="bg-[#0a0a0a] py-6 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center gap-12 text-white/30 text-xs font-mono tracking-widest">
            <span>SECURE & PRIVATE</span>
            <span className="hidden md:inline">•</span>
            <span>ED-TECH PLATFORM</span>
            <span className="hidden md:inline">•</span>
            <span>SELF-STUDY ONLY</span>
          </div>
        </div>
      </section> */}

      {/* Capabilities Section */}
      {/* <section id="capabilities" className="relative bg-[#111111]">
        <div className="px-6 md:px-12 lg:px-20 py-20">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-white/40 text-xs font-mono tracking-widest">CAPABILITIES</span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-light text-white mb-20">
              Built for deep understanding
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
              {capabilities.map((item, index) => (
                <div
                  key={index}
                  className="bg-[#111111] p-10 group hover:bg-white/[0.02] transition-all duration-500"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="p-3 border border-white/10 group-hover:border-accent\/50 transition-colors">
                      <item.icon className="h-6 w-6 text-accent" />
                    </div>
                  </div>
                  <h3 className="text-xl font-medium text-white mb-3 group-hover:text-accent transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-white/50 text-sm leading-relaxed">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section> */}

      {/* Fair Use Notice
      <section className="bg-[#0a0a0a] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-8">
          <div className="flex items-center justify-center gap-3 text-center">
            <BookOpen className="h-4 w-4 text-white/30" />
            <p className="text-white/40 text-sm">
              Designed for self-study and exam preparation only. Not intended for use during proctored assessments.
            </p>
          </div>
        </div>
      </section> */}

      {/* Quickstart Section */}
      <section id="download" className="relative bg-[#111111]">
        <div className="px-6 md:px-12 lg:px-20 py-20">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-white/40 text-xs font-mono tracking-widest">QUICKSTART</span>
                </div>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-light text-white mb-6">
                  Minutes to launch
                </h2>
                <p className="text-white/50 text-lg mb-8 max-w-lg">
                  Download, install, and start learning with just a few clicks.
                </p>

                {/* Installation Video */}
                <div className="mb-8">
                  <VideoPlayer
                    src={config.videos.installation}
                    title="INSTALLATION TUTORIAL"
                    id="landing-installation-video"
                  />
                </div>

                <div className="flex flex-wrap gap-4 mb-8">
                  <a
                    href={config.extensionDownloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black font-medium hover:bg-white/90 transition-colors"
                  >
                    <Download className="h-5 w-5" />
                    DOWNLOAD
                  </a>
                  <Link
                    to="/signup"
                    className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 text-white font-medium hover:bg-white/5 transition-colors"
                  >
                    <ArrowUpRight className="h-5 w-5" />
                    CREATE ACCOUNT
                  </Link>
                </div>

                <div className="flex items-center gap-6 text-white/40 text-sm">
                  <div className="flex items-center gap-2">
                    <Chrome className="h-4 w-4" />
                    <span>Chrome</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Monitor className="h-4 w-4" />
                    <span>Edge</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Monitor className="h-4 w-4" />
                    <span>Brave</span>
                  </div>
                </div>
              </div>

              <div className="space-y-px">
                {steps.map((step, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-6 p-6 bg-white/[0.02] border border-white/10 hover:border-white/20 transition-colors group"
                  >
                    <span className="text-3xl font-light text-white/20 group-hover:text-accent\/50 transition-colors font-mono">
                      {step.num}
                    </span>
                    <div>
                      <h4 className="text-lg font-medium text-white mb-1">{step.title}</h4>
                      <p className="text-white/50 text-sm">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Shortcuts Section */}
      <section className="relative bg-[#0a0a0a]">
        <div className="px-6 md:px-12 lg:px-20 py-20">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-white/40 text-xs font-mono tracking-widest">POWER USER</span>
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-light text-white mb-6">
                Keyboard shortcuts
              </h2>
              <p className="text-white/50 text-lg max-w-lg">
                Quick access to all features without lifting your hands from the keyboard.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              {/* Video Section with Toggle */}
              <div>
                <GuideVideoToggle />
              </div>

              {/* Shortcuts List */}
              <div className="space-y-px">
                {config.shortcuts.map((shortcut, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-5 bg-white/[0.02] border border-white/10 hover:border-white/20 transition-colors group"
                  >
                    <div className="flex items-center gap-2">
                      {shortcut.keys.map((key, i) => (
                        <React.Fragment key={i}>
                          <kbd className="px-3 py-1.5 text-xs font-mono bg-white/5 text-white/70 border border-white/10 group-hover:border-white/20 transition-colors">
                            {key}
                          </kbd>
                          {i < shortcut.keys.length - 1 && (
                            <span className="text-white/20">+</span>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                    <span className="text-white/60 text-sm font-medium">
                      {shortcut.action}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative bg-[#111111] overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          {/* <div 
            className="absolute top-0 right-0 w-1/2 h-full"
            style={{
              background: 'conic-gradient(from 180deg at 50% 50% in lab, rgba(255, 208, 134, 0.3) 0deg, rgba(17, 17, 17, 0) 90deg, rgba(17, 17, 17, 0) 270deg, rgba(255, 208, 134, 0.3) 360deg)'
            }}
          /> */}
        </div>
        <div className="relative px-6 md:px-12 lg:px-20 py-24">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-2xl">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-light text-white mb-6">
                Ready to understand more?
              </h2>
              <p className="text-white/50 text-lg mb-6">
                Join students who are learning smarter with AI-powered explanations.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/signup"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black font-medium hover:bg-white/90 transition-colors"
                >
                  Create Free Account
                  <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-2 px-8 py-4 border border-white/20 text-white font-medium hover:bg-white/5 transition-colors"
                >
                  View Pricing
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer variant="full" />
    </div>
  );
};

export default Landing;
